/**
 * Billing line items — the shared contract for "what's being billed" on a
 * fault or a service request, plus the tax math that turns those lines into
 * a subtotal / tax / total triple.
 *
 * One implementation, consumed by:
 *   client: BillingCaptureModal, fault.service.ts, service-request.service.ts,
 *           FaultBillingCard, ServiceRequestBillingCard, BillingPage
 *   server: functions/src/mcp/billing.ts (the connector's update_billing)
 *
 * ── MONEY BASIS (non-negotiable) ───────────────────────────────────────────
 * `unitPrice` is ALWAYS ex-tax, in every tax mode, matching the repo-wide
 * GST basis map (catalog/quote line-item unitPrice is ex-tax). `taxMode` is
 * an ENTRY + PRESENTATION concern, never a second storage basis:
 *
 *   'exclusive' — the office typed ex-tax prices; total = subtotal + tax
 *   'inclusive' — the office typed tax-inclusive prices; the editor backs the
 *                 tax out on entry (`exFromIncPrice`) and stores ex-tax, so
 *                 the headline figure is the tax-inclusive total
 *   'none'      — nothing on this job attracts tax; tax is 0 regardless of
 *                 any per-line `taxable` flag
 *
 * Per-line `taxable` (default TRUE) handles the mixed case — a tax-free line
 * sitting among taxable ones.
 *
 * PURITY CONTRACT (lint-enforced for all of shared/): no React, no firebase,
 * no node builtins, no ambient state.
 */
import { roundMoney, toFiniteMoney } from './round';
import { resolveTaxRate } from './tax';

/** How the captured prices relate to tax. See the module header. */
export type BillingTaxMode = 'exclusive' | 'inclusive' | 'none';

export const BILLING_TAX_MODES: readonly BillingTaxMode[] = [
  'exclusive',
  'inclusive',
  'none',
];

/**
 * Default when a doc predates tax-mode capture: prices were ex-tax.
 *
 * READ-PATH ONLY. Do not repurpose this as the default for a NEW capture —
 * it is what a stored doc with no `billingTaxMode` MEANT, and flipping it
 * would retro-reinterpret every historical capture's numbers.
 */
export const DEFAULT_BILLING_TAX_MODE: BillingTaxMode = 'exclusive';

/**
 * Default entry basis for a FRESH capture (no stored mode to honour).
 *
 * Staff quote and discuss every customer-facing price tax-inclusive, so that
 * is what the empty box asks for. Distinct from DEFAULT_BILLING_TAX_MODE
 * above, which is the read-path meaning of a doc that never stored a mode —
 * those really were ex-tax and must keep resolving that way.
 */
export const NEW_CAPTURE_TAX_MODE: BillingTaxMode = 'inclusive';

/**
 * Where a line came from. Presentation only (badge + grouping) — it never
 * changes the math, so an unknown value from an older doc is harmless.
 */
export type BillingLineKind = 'part' | 'labour' | 'callout' | 'custom' | 'discount';

/**
 * A single "what's being billed" line. Sell-side (what the customer is
 * charged), distinct from internal cost tracking (`fault.costs.materials`).
 */
export interface BillingLineItem {
  /** Stable id for editor keying. Optional — older stored lines have none. */
  id?: string;
  description: string;
  quantity: number;
  /** Ex-tax unit price. May be NEGATIVE for a credit / discount line. */
  unitPrice: number;
  /** Tax applies to this line. Absent === true (every legacy line was taxed). */
  taxable?: boolean;
  /** Set when the line was picked from the parts catalog (Parts Hub). */
  partsCatalogId?: string;
  sku?: string;
  kind?: BillingLineKind;
}

/** The money triple every billing surface renders. All figures rounded to cents. */
export interface BillingTotals {
  /** Ex-tax sum of every line, taxable or not. */
  subtotalEx: number;
  /** Ex-tax sum of the taxable lines only — the base the tax is charged on. */
  taxableEx: number;
  /** Ex-tax sum of the tax-free lines. */
  nonTaxableEx: number;
  /** Tax charged. Always 0 in 'none' mode. */
  tax: number;
  /** subtotalEx + tax. */
  totalIncTax: number;
  /** Number of lines that contributed. */
  lineCount: number;
}

export const EMPTY_BILLING_TOTALS: BillingTotals = {
  subtotalEx: 0,
  taxableEx: 0,
  nonTaxableEx: 0,
  tax: 0,
  totalIncTax: 0,
  lineCount: 0,
};

/** A line is taxable unless it explicitly says otherwise. */
export function isLineTaxable(item: Pick<BillingLineItem, 'taxable'>): boolean {
  return item.taxable !== false;
}

/** Ex-tax total for one line: quantity × unitPrice, rounded to cents. */
export function billingLineTotal(
  item: Pick<BillingLineItem, 'quantity' | 'unitPrice'>,
): number {
  const qty = toFiniteMoney(item.quantity) ?? 0;
  const price = toFiniteMoney(item.unitPrice) ?? 0;
  return roundMoney(qty * price);
}

/** Back the tax out of a tax-inclusive price → the ex-tax price. */
export function exFromIncPrice(inc: number, rate: unknown): number {
  const r = resolveTaxRate(rate);
  const n = toFiniteMoney(inc) ?? 0;
  return roundMoney(n / (1 + r));
}

/** Gross an ex-tax price up to tax-inclusive. */
export function incFromExPrice(ex: number, rate: unknown): number {
  const r = resolveTaxRate(rate);
  const n = toFiniteMoney(ex) ?? 0;
  return roundMoney(n * (1 + r));
}

/**
 * A TYPED gross amount → the ex-tax basis `billingAmount` stores.
 *
 * Mode-aware on purpose. The flat "Set amount" editors on the billing cards
 * ask for the gross figure (what the customer is invoiced), but a capture
 * recorded with `taxMode: 'none'` is GST-FREE — its stored amount already IS
 * the gross. Dividing that by (1 + rate) would quietly shave the tax rate off
 * every GST-free callout. `'exclusive'` and `'inclusive'` both store ex-tax,
 * so both convert.
 */
export function exFromTypedGross(
  gross: number,
  rate: unknown,
  mode: BillingTaxMode,
): number {
  if (mode === 'none') return roundMoney(toFiniteMoney(gross) ?? 0);
  return exFromIncPrice(gross, rate);
}

/** Inverse of exFromTypedGross — seeds the editor from the stored amount. */
export function typedGrossFromEx(
  ex: number,
  rate: unknown,
  mode: BillingTaxMode,
): number {
  if (mode === 'none') return roundMoney(toFiniteMoney(ex) ?? 0);
  return incFromExPrice(ex, rate);
}

/**
 * Coerce untrusted stored/captured lines into the canonical shape, dropping
 * anything unusable. Tolerant by design — Firestore holds lines written by
 * three different call paths across two entity types, some pre-dating the
 * `taxable` / `kind` fields, some with string-typed numbers from console
 * edits.
 *
 * Kept: a non-empty description AND a finite unit price (zero and negative
 * both allowed — $0 warranty items and negative credit lines are real).
 * Quantity heals to 1 when missing or non-positive.
 */
export function normalizeBillingLines(raw: unknown): BillingLineItem[] {
  if (!Array.isArray(raw)) return [];
  const out: BillingLineItem[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const it = entry as Record<string, unknown>;
    const description = typeof it.description === 'string' ? it.description.trim() : '';
    if (!description) continue;
    const unitPrice = toFiniteMoney(it.unitPrice);
    if (unitPrice === null) continue;
    const qty = toFiniteMoney(it.quantity);
    const line: BillingLineItem = {
      description,
      quantity: qty !== null && qty > 0 ? qty : 1,
      unitPrice: roundMoney(unitPrice),
    };
    if (typeof it.id === 'string' && it.id) line.id = it.id;
    // Only persist the flag when it's the non-default (false) — keeps stored
    // docs identical in shape to the pre-`taxable` lines they replace.
    if (it.taxable === false) line.taxable = false;
    if (typeof it.partsCatalogId === 'string' && it.partsCatalogId) {
      line.partsCatalogId = it.partsCatalogId;
    }
    if (typeof it.sku === 'string' && it.sku) line.sku = it.sku;
    if (typeof it.kind === 'string' && it.kind) line.kind = it.kind as BillingLineKind;
    out.push(line);
  }
  return out;
}

/**
 * Roll lines up into subtotal / tax / total.
 *
 * Tax is computed on the SUM of the taxable lines, not per line — summing
 * per-line rounded tax drifts from the invoice a cent at a time on longer
 * itemisations. Xero and the Theo invoice pipeline both charge tax on the
 * subtotal, so this matches what the customer is eventually invoiced.
 */
export function computeBillingTotals(
  items: readonly BillingLineItem[] | null | undefined,
  rate: unknown,
  mode: BillingTaxMode = DEFAULT_BILLING_TAX_MODE,
): BillingTotals {
  const lines = items ?? [];
  if (lines.length === 0) return { ...EMPTY_BILLING_TOTALS };

  const r = resolveTaxRate(rate);
  let taxableEx = 0;
  let nonTaxableEx = 0;
  for (const line of lines) {
    const total = billingLineTotal(line);
    if (mode !== 'none' && isLineTaxable(line)) taxableEx += total;
    else nonTaxableEx += total;
  }
  taxableEx = roundMoney(taxableEx);
  nonTaxableEx = roundMoney(nonTaxableEx);
  const subtotalEx = roundMoney(taxableEx + nonTaxableEx);
  const tax = mode === 'none' ? 0 : roundMoney(taxableEx * r);

  return {
    subtotalEx,
    taxableEx,
    nonTaxableEx,
    tax,
    totalIncTax: roundMoney(subtotalEx + tax),
    lineCount: lines.length,
  };
}

/**
 * Totals for an entity billed with a flat amount and no itemisation — the
 * pre-line-items path, still reachable from the connector and from the
 * billing card's inline "Set amount" edit.
 */
export function computeFlatBillingTotals(
  amountEx: unknown,
  rate: unknown,
  mode: BillingTaxMode = DEFAULT_BILLING_TAX_MODE,
): BillingTotals {
  const subtotalEx = roundMoney(toFiniteMoney(amountEx) ?? 0);
  const tax = mode === 'none' ? 0 : roundMoney(subtotalEx * resolveTaxRate(rate));
  return {
    subtotalEx,
    taxableEx: mode === 'none' ? 0 : subtotalEx,
    nonTaxableEx: mode === 'none' ? subtotalEx : 0,
    tax,
    totalIncTax: roundMoney(subtotalEx + tax),
    lineCount: 0,
  };
}

/** Narrow an untrusted value to a BillingTaxMode, defaulting to 'exclusive'. */
export function resolveBillingTaxMode(value: unknown): BillingTaxMode {
  return BILLING_TAX_MODES.includes(value as BillingTaxMode)
    ? (value as BillingTaxMode)
    : DEFAULT_BILLING_TAX_MODE;
}

/**
 * One-line human summary of an itemisation, for timeline/activity entries
 * and Slack copy: "3× Callout fee, 1× PoE injector".
 */
export function summarizeBillingLines(
  items: readonly BillingLineItem[] | null | undefined,
  maxItems = 4,
): string {
  const lines = items ?? [];
  if (lines.length === 0) return '';
  const head = lines
    .slice(0, maxItems)
    .map((l) => `${l.quantity}× ${l.description}`)
    .join(', ');
  const rest = lines.length - maxItems;
  return rest > 0 ? `${head} +${rest} more` : head;
}
